import React, {
  Dispatch,
  SetStateAction,
  useState,
  useCallback
} from 'react';
import { calendarGroup, IPlannerGroup } from 'model/calendar/groups';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV } from '@fortawesome/free-solid-svg-icons';
import { CloseOutlined } from '@ant-design/icons';
import EditIcon from '@material-ui/icons/Edit';
import { getAttendeeBulletClassName } from 'helpers/calendarUIHelper';
import { useReactiveVar } from '@apollo/client';
import {
  plannerCalendarPrefViewVar,
  PlannerView,
  providerAppointmentHours
} from 'utils/cache/calendar';
import { providerResourcesPrevVar } from 'utils/cache/filters';
import { GET_CLIENT_BASIC_DATA } from 'api/graphql/v2/queries/Clients';
import { useQuery } from 'react-apollo';
import { BasicClientProfile } from 'model/client';
import moment from 'moment-timezone';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import { ROUTE_PATHS, TARGET_BLANK } from 'utils/constants/routes';
import EditTargetSessionsModal from 'components/calendar/planner/PlannerModals/EditTargetSessionsModal';
import { IWeeklySessionsData } from 'model/v2/providerCapacity';
import { truncateAttendeeLabel } from 'utils/format';

const PLANNER_DAY_TITLE_MAX_CHARS = 30;

interface Props {
  cloneGroup: boolean;
  group: IPlannerGroup | undefined;
  isShown: boolean;
  setRemovedGroups: Dispatch<SetStateAction<number[]>>;
  removedGroups: number[];
  showFullGroup: boolean;
  handleGroupRemoval?: () => void;
  dragHandleProps?: DraggableProvidedDragHandleProps;
}

const Group: React.FC<Props> = ({
  cloneGroup,
  group,
  isShown,
  showFullGroup,
  handleGroupRemoval,
  dragHandleProps
}: Props) => {
  const calendarView = useReactiveVar(plannerCalendarPrefViewVar);
  const isClient = group?.attendeeType === calendarGroup.client;
  const hoursData = useReactiveVar(providerAppointmentHours);
  // AXON-3578 is DT-only. DT rows show session counts and nothing else; every
  // other department keeps the panel it had before that ticket.
  const isDTProvider = group?.speciality?.department === 'DT';

  const [clientData, setClientData] = React.useState<BasicClientProfile>();
  const [isEditSessionsModalOpen, setIsEditSessionsModalOpen] = useState(false);

  let idForRequest: string | number | undefined;
  if (group?.id && typeof group.id === 'string') {
    const idString = group.id.toString();
    const idStringList = idString.split(':');
    idForRequest = parseInt(idStringList[0]);
  } else {
    idForRequest = group?.id;
  }

  const handleEditSessionsClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditSessionsModalOpen(true);
  }, []);

  const handleEditSessionsClose = useCallback(() => {
    setIsEditSessionsModalOpen(false);
  }, []);

  const handleUpdateTargetSessions = useCallback(
    (sessions: IWeeklySessionsData) => {
      // Update the local cache with the new target sessions
      const providers = providerResourcesPrevVar();
      const updatedProviders = providers.map(provider => {
        if (
          provider.id === idForRequest ||
          provider.baseId === String(idForRequest)
        ) {
          return {
            ...provider,
            providerCapacity: {
              ...provider.providerCapacity,
              providerTargetSessions: sessions
            }
          };
        }
        return provider;
      });
      providerResourcesPrevVar(updatedProviders as any);
    },
    [idForRequest]
  );

  const formatClientDob = (date: string) => {
    const result = moment(date, 'YYYY-MM-DD').format('MM-DD-YYYY');
    return result;
  };

  // Use loading state directly from useQuery - properly reflects query lifecycle
  // For providers (skip: true), loading is always false
  // For clients, loading is true during query, false after completion/error
  const { loading: isLoading } = useQuery(GET_CLIENT_BASIC_DATA, {
    fetchPolicy: 'no-cache',
    variables: { id: Number(idForRequest) },
    skip: !isClient, // Don't run this query for providers
    onCompleted: data => {
      setClientData(data.client);
    }
  });

  return (
    <div className={cloneGroup ? 'custom-group clone' : 'custom-group'}>
      <div className="group-identifier">
        {isShown && !cloneGroup ? (
          <FontAwesomeIcon icon={faEllipsisV} className="drag-icon" />
        ) : (
          <div
            style={{
              width:
                group?.attendeeType === calendarGroup.client ? '10px' : '16px'
            }}
          />
        )}
        {showFullGroup && (
          <div
            className={getAttendeeBulletClassName(group)}
            {...dragHandleProps}
          />
        )}
        {showFullGroup && (
          <div
            style={{ display: 'flex', flexDirection: 'column', width: '100%' }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between'
              }}
            >
              <div {...dragHandleProps} style={{ flex: 1 }}>
                <a
                  href={
                    isClient
                      ? `${ROUTE_PATHS.CLIENT_AVAILABILITY}${idForRequest}`
                      : `${ROUTE_PATHS.PROVIDER}${idForRequest}`
                  }
                  target={TARGET_BLANK}
                  rel="noopener noreferrer"
                  className="title-link"
                  onClick={e => e.stopPropagation()}
                >
                  <p className="title">
                    {showFullGroup
                      ? calendarView === PlannerView.plannerDay
                        ? truncateAttendeeLabel(
                            group?.displayName,
                            PLANNER_DAY_TITLE_MAX_CHARS
                          )
                        : group?.displayName
                      : ''}
                  </p>
                </a>
              </div>
              {showFullGroup && (
                <button
                  className={`ui icon button ${
                    cloneGroup ? 'clone-close-button' : 'close-button'
                  }`}
                  style={{ margin: 0, padding: 0 }}
                  onClick={handleGroupRemoval}
                >
                  <CloseOutlined />
                </button>
              )}
            </div>
            {!isClient && group?.speciality?.department === 'DT' && (
              <span
                className="sessions-badge"
                style={{
                  backgroundColor: (() => {
                    const targetTotal = group?.providerCapacity?.providerTargetSessions?.total ?? 0;
                    const bookedTotal = group?.providerCapacity?.providerBookedSessions?.total ?? 0;
                    if (targetTotal === 0) return 'transparent';
                    if (targetTotal > bookedTotal) return '#F4B3B3';
                    if (targetTotal <= bookedTotal) return '#D3E9D5';
                    return 'transparent';
                  })(),
                  border: (group?.providerCapacity?.providerTargetSessions?.total ?? 0) === 0
                    ? '1px solid #E0E0E0'
                    : 'none'
                }}
              >
                {group?.providerCapacity?.providerBookedSessions?.total ?? 0}/
                {group?.providerCapacity?.providerTargetSessions?.total ?? 0}{' '}
                total sessions scheduled
                {/* AXON-3559: Edit the weekly session limit from the Planner */}
                <EditIcon
                  className="sessions-edit-icon"
                  onClick={handleEditSessionsClick}
                />
              </span>
            )}
            {!isClient && group?.speciality?.department === 'DT' && (
              <EditTargetSessionsModal
                isOpen={isEditSessionsModalOpen}
                onClose={handleEditSessionsClose}
                providerName={group?.displayName || ''}
                providerId={idForRequest as number}
                targetSessions={
                  (group?.providerCapacity
                    ?.providerTargetSessions as IWeeklySessionsData) ?? null
                }
                onUpdate={handleUpdateTargetSessions}
              />
            )}
            {isClient && clientData && !isLoading && (
              <div className="client-case-render">
                <div className="basic-data-shell">
                  <div className="basic-info">
                    <div className="basic-info-block">
                      <small style={{ lineHeight: '12px' }}>
                        Athena ID: {clientData.athenaId}
                      </small>
                    </div>
                    <div className="basic-info-block">
                      <small style={{ lineHeight: '12px' }}>
                        CR ID: {clientData.crId}
                      </small>
                    </div>
                  </div>
                  <div className="dob-row" style={{ lineHeight: '13px' }}>
                    <small>
                      DOB: {formatClientDob(clientData.profile.dob)}
                    </small>
                  </div>
                </div>
                {calendarView === PlannerView.plannerWeek && (
                  <div className="advance-info-shell">
                    <div className="advance-info-shell-child">
                      <small style={{ lineHeight: '13px' }}>
                        Legal Guardian(s):{' '}
                        {clientData.profile.legalGuardianFirstName}{' '}
                        {clientData.profile.legalGuardianLastName}
                      </small>
                    </div>
                    <div className="advance-info-shell-child">
                      <small style={{ lineHeight: '12px' }}>
                        <div className="spaced-text">Contact Email: </div>
                        <div className="spaced-text">
                          {clientData.profile.personalEmail
                            ? clientData.profile.personalEmail
                            : 'N/A'}
                        </div>
                      </small>
                    </div>
                    <div className="advance-info-shell-child">
                      <small style={{ lineHeight: '12px' }}>
                        <div className="spaced-text">Mother's Email: </div>
                        <div className="spaced-text">
                          {clientData.profile.motherEmail
                            ? clientData.profile.motherEmail
                            : 'N/A'}
                        </div>
                      </small>
                    </div>
                    <div className="advance-info-shell-child">
                      <small style={{ lineHeight: '12px' }}>
                        <div className="spaced-text">Father's Email: </div>
                        <div className="spaced-text">
                          {clientData.profile.fatherEmail
                            ? clientData.profile.fatherEmail
                            : 'N/A'}
                        </div>
                      </small>
                    </div>
                  </div>
                )}
              </div>
            )}
            {!isClient &&
              !isLoading &&
              !isDTProvider &&
              calendarView === PlannerView.plannerWeek && (
                <div className="client-case-render">
                  <div className="advance-info-shell">
                    <small className="advance-info-shell-child">
                      Programs:{' '}
                      {group?.corticaPrograms
                        ?.map(program => program.programName)
                        .join(', ')}
                    </small>
                  </div>
                </div>
              )}
            {/* AXON-3578: the DT teams asked for a panel showing session counts
                alone. That feedback is DT-specific, so ABA, MED and FWC keep
                certifications and the paycode-derived hours they always had. */}
            {!isClient &&
              !isLoading &&
              !isDTProvider &&
              calendarView === PlannerView.plannerWeek && (
                <div className="client-case-render">
                  <div className="advance-info-shell">
                    <small className="advance-info-shell-child">
                      Certs:{' '}
                      {group?.certificationMappings?.map(
                        cert =>
                          `${cert.certification?.certificationName}: ${moment(
                            cert.renewalDate
                          )
                            .tz('America/Los_Angeles')
                            .format('MM/DD/YY')} `
                      )}
                    </small>
                  </div>
                </div>
              )}

            {!isDTProvider &&
              hoursData[group?.baseId!] !== undefined &&
              calendarView === PlannerView.plannerWeek && (
                <>
                  <div className="client-case-render">
                    <div className="advance-info-shell">
                      <small className="advance-info-shell-child">
                        Direct Hours: {hoursData[group?.baseId!].directHours}
                      </small>
                    </div>
                  </div>

                  {hoursData[group?.baseId!].isABA && (
                    <div className="client-case-render">
                      <div className="advance-info-shell">
                        <small className="advance-info-shell-child">
                          Indirect Hours:{' '}
                          {hoursData[group?.baseId!].indirecthours}
                        </small>
                      </div>
                    </div>
                  )}

                  <div className="client-case-render">
                    <div className="advance-info-shell">
                      <small className="advance-info-shell-child">
                        Total Weekly Hours:{' '}
                        {hoursData[group?.baseId!].totalHours}
                      </small>
                    </div>
                  </div>
                </>
              )}
            {!isClient &&
              !isLoading &&
              !isDTProvider &&
              calendarView === PlannerView.plannerWeek && (
                <div className="client-case-render">
                  <div className="advance-info-shell">
                    <small className="advance-info-shell-child">
                      Meal Waiver: {group?.isMealWaiver ? 'Yes' : 'No'}
                    </small>
                  </div>
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(Group, (prevProps, nextProps) => {
  // Custom comparison to detect providerCapacity changes
  const prevCapacity = prevProps.group?.providerCapacity;
  const nextCapacity = nextProps.group?.providerCapacity;
  
  // If providerCapacity changed, re-render
  if (
    prevCapacity?.providerBookedSessions !== nextCapacity?.providerBookedSessions ||
    prevCapacity?.providerTargetSessions !== nextCapacity?.providerTargetSessions
  ) {
    return false; // false means re-render
  }
  
  // Default shallow comparison for other props
  return (
    prevProps.group?.id === nextProps.group?.id &&
    prevProps.cloneGroup === nextProps.cloneGroup &&
    prevProps.isShown === nextProps.isShown &&
    prevProps.showFullGroup === nextProps.showFullGroup &&
    prevProps.removedGroups === nextProps.removedGroups
  );
});
